import { inject, Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { catchError, switchMap } from 'rxjs/operators';

import { ConfigService } from '../../tms-shared';
import { ApplicationPaths } from '../constants';
import { AuthService } from '../services';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly configService = inject(ConfigService);
  private readonly router = inject(Router);

  private tokenRefreshed$ = new Subject<void>();
  private refreshTokenInProgress = false;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return this.addAuthHeaderAndProcess(request, next).pipe(
      catchError((error: any) => this.handleError(error, request, next)),
    );
  }

  private addAuthHeaderAndProcess(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.authService
      .getAccessToken()
      .pipe(switchMap((token: string) => this.processRequestWithToken(token, req, next)));
  }

  private processRequestWithToken(token: string, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!!token && this.isSameOriginUrl(req)) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return next.handle(req);
  }

  private isSameOriginUrl(req: any): boolean {
    return (
      req.url.startsWith(`${this.configService.config.apiUrl}/`) ||
      req.url.startsWith(`${this.configService.config.identityServerUrl}/`)
    );
  }

  private handleError(error: any, request: HttpRequest<any>, next: HttpHandler) {
    switch (error.status) {
      case 401:
        return this.refreshToken().pipe(
          switchMap(() => this.addAuthHeaderAndProcess(request, next)),
          catchError((retryError: any) => {
            if (retryError.status === 401) {
              this.logout();
            } else {
              return this.handleError(retryError, request, next);
            }
          }),
        );
      case 404:
        console.log(`404 Not Found: ${error.url}`);
        break;
      default:
        throw error;
    }
  }

  private refreshToken(): Observable<any> {
    if (this.refreshTokenInProgress) {
      return this.tokenRefreshed$;
    } else {
      this.refreshTokenInProgress = true;
      const state: any = { returnUrl: location.pathname };

      return fromPromise(
        this.authService.signIn(state).then(
          () => {
            this.refreshTokenInProgress = false;
            this.tokenRefreshed$.next();
          },
          () => {
            this.refreshTokenInProgress = false;
            this.logout();
          },
        ),
      );
    }
  }

  private logout(): Promise<boolean> {
    return this.router.navigate(ApplicationPaths.LogOutPathComponents);
  }
}
